import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.REACT_APP_NAVIGATE(`/category/all?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setSearchOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-100/95 backdrop-blur-md border-b border-background-200'
          : 'bg-transparent'
      }`}
    >
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <span className="text-2xl font-bold font-heading text-accent-500">51</span>
            <span className="text-lg font-bold font-heading text-foreground-100">视频</span>
          </Link>

          <div className="hidden lg:flex items-center gap-6">
            {categories.slice(1, 6).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`text-sm whitespace-nowrap transition-colors duration-200 cursor-pointer ${
                  location.pathname.includes(cat.slug)
                    ? 'text-accent-500'
                    : 'text-foreground-300 hover:text-foreground-100'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200/70 hover:bg-background-300 transition-colors cursor-pointer"
              aria-label="搜索"
            >
              <i className="ri-search-line text-foreground-200 text-lg"></i>
            </button>

            <Link
              to="/about"
              className="hidden md:flex text-sm text-foreground-300 hover:text-foreground-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              关于我们
            </Link>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full bg-background-200/70 hover:bg-background-300 transition-colors cursor-pointer"
              aria-label="菜单"
            >
              <i className={`text-foreground-200 text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="py-3 border-t border-background-200">
            <form onSubmit={handleSearch} className="flex items-center gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索视频、文章..."
                className="flex-1 bg-background-200 border border-background-300 rounded-lg px-4 py-2 text-sm text-foreground-100 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500 transition-colors"
                autoFocus
              />
              <button
                type="submit"
                className="px-4 py-2 bg-accent-500 hover:bg-accent-600 text-background-950 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>
          </div>
        )}
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-background-100/95 backdrop-blur-md border-t border-background-200">
          <div className="px-4 md:px-6 py-4 flex flex-col gap-2">
            {categories.slice(1).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${
                  location.pathname.includes(cat.slug)
                    ? 'bg-accent-500/10 text-accent-500'
                    : 'text-foreground-300 hover:bg-background-200 hover:text-foreground-100'
                }`}
              >
                <i className={`${cat.icon} text-lg`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}